import classNames from 'classnames'
import { useLanguages } from 'components/hooks/language'
import { useEffect, useMemo, useState } from 'react'
import { ChatTextInput, ChatVoiceInput, ChatPushToTalkInput } from 'ui'
import ChatBotOptionBar from './chatBotOptionBar'
import { T, useTranslate } from '@tolgee/react'
import useTranscribe from 'components/hooks/transcribe'
import { ContextMenu } from 'components/context-menu'
import { useGetTranscription } from 'toolComponents/generic/audio/transcription'
import Lottie from 'react-lottie'
import * as animationData from './sparkle-lottie.json'

const defaultOptions = {
  loop: true,
  autoplay: true,
  animationData: animationData,
  rendererSettings: {
    preserveAspectRatio: 'xMidYMid slice',
  },
}

const ChatBotForm = ({
  isGenerating,
  handleSubmit,
  handleStopRequest,
  setMessage,
  message,
  language,
  handleClearChat,
  userCanAccessHistory,
  setHistoryIsVisible,
  isAutoAudio,
  audioStream,
  setTtsIndex,
  setAudioStream,
  setIsAutoAudio,
  setLanguage,
  isSearchWeb,
  isSearchWebAllowed,
  setSearchWeb,
  isLanguageChangeAllowed,
  isModeChangeAllowed,
  isContextAllowed,
  documentContext,
  isExternal,
  messages,
  defaultMode,
  isHistoryVisible,
  isFocused = true,
}) => {
  const { languages } = useLanguages()
  const { t } = useTranslate()

  const [inputMode, setInputMode] = useState(defaultMode ?? 'text')

  useEffect(() => {
    if (inputMode === 'text') {
      setIsAutoAudio(false)
    } else {
      setIsAutoAudio(true)
    }
  }, [inputMode, setIsAutoAudio])

  const handleVolumeOff = () => {
    setIsAutoAudio(false)
    setAudioStream()
    setTtsIndex()
  }

  const textInput = useMemo(
    () => (
      <ChatTextInput
        state={{ language: language, inputValue: message }}
        dispatch={(e) => {
          if (e.type === 'SET_INPUT') {
            setMessage(e.payload)
          }
        }}
        handleSubmit={handleSubmit}
        isGenerating={isGenerating}
        handleStopRequest={handleStopRequest}
        enableEnhance={false}
        isChatbot
        t={t}
        useTranscribe={useTranscribe}
        ContextMenu={ContextMenu}
        audioStream={audioStream}
        setAudioStream={setAudioStream}
      />
    ),
    [
      language,
      message,
      setMessage,
      handleSubmit,
      isGenerating,
      handleStopRequest,
      t,
      audioStream,
      setAudioStream,
    ]
  )

  const pushToTalkInput = useMemo(
    () => (
      <ChatPushToTalkInput
        state={{ language: languages.find((lang) => lang.value === language) ?? language }}
        handleSubmit={handleSubmit}
        isGenerating={isGenerating}
        handleStopRequest={handleStopRequest}
        audioStream={audioStream}
        setAudioStream={setAudioStream}
        isChatbot
        t={t}
        T={T}
        useGetTranscription={useGetTranscription}
        loader={<Lottie options={defaultOptions} height={24} width={24} />}
        isFocused={isFocused}
      />
    ),
    [
      isGenerating,
      handleSubmit,
      handleStopRequest,
      audioStream,
      setAudioStream,
      language,
      languages,
      t,
      isFocused,
    ]
  )

  const voiceInput = useMemo(
    () => (
      <ChatVoiceInput
        state={{ language: language }}
        dispatch={(e) => {
          if (e.type === 'SET_INPUT') {
            setMessage(e.payload)
          }
        }}
        handleSubmit={handleSubmit}
        audioStream={audioStream}
        setAudioStream={setAudioStream}
        chatLog={messages}
        isChatbot
        t={t}
        T={T}
        useTranscribe={useTranscribe}
      />
    ),
    [handleSubmit, audioStream, setAudioStream, messages, language, setMessage, t]
  )

  const inputs = {
    text: textInput,
    read: textInput,
    voice: voiceInput,
    push: pushToTalkInput,
  }

  return (
    <div
      className={classNames(isExternal ? 'bg-brand-grey-bg' : 'bg-brand-chat-background', 'w-full')}
    >
      <div className='flex w-full flex-col items-center gap-[10px] p-2 py-3 sm:gap-[16px] sm:p-4 md:px-[30px] md:py-[40px] lg:gap-[24px] 2xl:px-[72px] 2xl:py-[40px]'>
        {inputs[inputMode]}

        <ChatBotOptionBar
          language={language}
          languages={languages}
          setLanguage={setLanguage}
          isAutoAudio={isAutoAudio}
          audioStream={audioStream}
          handleVolumeOff={handleVolumeOff}
          setIsAutoAudio={setIsAutoAudio}
          userCanAccessHistory={userCanAccessHistory}
          setHistoryIsVisible={setHistoryIsVisible}
          isHistoryVisible={isHistoryVisible}
          handleClearChat={handleClearChat}
          isSearchWebAllowed={isSearchWebAllowed}
          isSearchWeb={isSearchWeb}
          isContextAllowed={isContextAllowed}
          setSearchWeb={setSearchWeb}
          isLanguageChangeAllowed={isLanguageChangeAllowed}
          documentContext={documentContext}
          isExternal={isExternal}
          inputMode={inputMode}
          setInputMode={setInputMode}
          isModeChangeAllowed={isModeChangeAllowed}
        />
      </div>
    </div>
  )
}

export default ChatBotForm
